import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/Toaster';
import { errMsg } from '../utils/errMsg';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../services/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/models/schemas.py NotificationResponse)
// ---------------------------------------------------------------------------

export type NotificationKind =
  | 'quota'
  | 'sustained_failure'
  | 'monitor_health'
  | 'stale_run'
  | 'sheet_drift'
  // Admin-only capacity alerts (ADR-53); the API only sends them to admins.
  | 'run_queue'
  | 'pod_memory'
  | 'schedule_unpinned'
  // ADR-56: schedule escalation — requests and responses go to admins,
  // decisions to the person who asked.
  | 'schedule_request'
  | 'schedule_response'
  | 'schedule_decision';

export type NotificationSeverity = 'critical' | 'warning' | 'info';

export interface AppNotification {
  id: string;
  dedupe_key: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body?: string | null;
  subject?: string | null;
  occurrences: number;
  created_at: string;
  last_seen_at: string;
  resolved_at?: string | null;
  read: boolean;
}

export interface NotificationPayload {
  notifications: AppNotification[];
  unread_count: number;
  unresolved_count: number;
}

// The bell polls; 60s is frequent enough to notice a broken table within a
// couple of cron cycles without hammering BigQuery for five users.
const POLL_MS = 60000;

export const KIND_LABELS: Record<NotificationKind, string> = {
  quota: 'Quota',
  sustained_failure: 'Failing table',
  monitor_health: 'Monitoring',
  stale_run: 'Stuck run',
  sheet_drift: 'Spreadsheet change',
  run_queue: 'Run queue',
  pod_memory: 'Memory',
  schedule_unpinned: 'Schedules',
  schedule_request: 'Schedule request',
  schedule_response: 'Schedule request',
  schedule_decision: 'Your schedule request',
};

/** Tailwind classes per severity. Every colour used here has a matching
 *  `.dark` override in index.css — add one if you introduce a new tone. */
export const SEVERITY_STYLES: Record<
  NotificationSeverity,
  { pill: string; dot: string; label: string }
> = {
  critical: {
    pill: 'bg-fail-bg text-fail-fg',
    dot: 'bg-fail-fg',
    label: 'Critical',
  },
  warning: {
    pill: 'bg-warn-bg text-warn-fg',
    dot: 'bg-warn-fg',
    label: 'Warning',
  },
  info: {
    pill: 'bg-info-bg text-info-fg',
    dot: 'bg-info-fg',
    label: 'Info',
  },
};

export const relTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useNotifications(includeResolved = false) {
  return useQuery({
    queryKey: ['notifications', includeResolved],
    queryFn: () =>
      getNotifications(includeResolved).then(
        (r) => r.data as NotificationPayload,
      ),
    refetchInterval: POLL_MS,
    // Counters should keep ticking while the tab is backgrounded-then-focused.
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (e: any) =>
      toast.error(errMsg(e, 'Failed to mark the notification read.')),
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('All notifications marked as read.');
    },
    onError: (e: any) =>
      toast.error(errMsg(e, 'Failed to mark notifications read.')),
  });
}
